import React, { useState, useEffect } from 'react';
import BBALoader from '../BBALoader';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getUserGoals } from '../../services/firebase/userGoalsService';

const formatGoalValue = (value, unit) => {
  if (value === null || value === undefined) return '0';
  if (unit === 'usd') return `$${Number(value).toLocaleString()}`;
  if (unit === 'percent') return `${value}%`;
  return value.toString();
};

/**
 * Compact card showing up to three goals for a department, each with a progress
 * bar, plus a link to all goals.
 */
const DepartmentGoals = ({ department, ownerId, ownerLoading = false, maxGoals = 3, className = '' }) => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ownerLoading) return undefined;
    if (!ownerId) {
      setGoals([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const allGoals = await getUserGoals(ownerId);
        if (!cancelled) setGoals(allGoals?.[department] || []);
      } catch (error) {
        console.error('Error loading department goals:', error);
        if (!cancelled) setGoals([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ownerId, ownerLoading, department]);

  const busy = loading || ownerLoading;
  const topGoals = goals.slice(0, maxGoals);

  const cardClass = `rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-4 flex flex-col ${className}`;

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="text-base leading-none" aria-hidden="true">🎯</span>
        <h2 className="font-semibold text-gray-800 dark:text-white">Goals</h2>
      </div>

      {busy ? (
        <div className="flex items-center justify-center flex-1 py-6">
          <BBALoader size={24} />
        </div>
      ) : topGoals.length > 0 ? (
        <div className="flex flex-col gap-3 flex-1">
          {topGoals.map((goal, index) => {
            const progress = goal.target > 0 ? (goal.actual / goal.target) * 100 : 0;
            const pct = Math.max(0, Math.min(100, progress));
            const barColor =
              progress >= 100 ? 'bg-emerald-500' : progress >= 70 ? 'bg-amber-500' : 'bg-red-500';
            return (
              <div key={goal.id || index}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-800 dark:text-white truncate">
                    {goal.metricName}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {Math.round(pct)}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden mb-1">
                  <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatGoalValue(goal.actual, goal.unit)}</span>
                  <span>Target {formatGoalValue(goal.target, goal.unit)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-center text-gray-500 dark:text-gray-400 flex-1 flex items-center justify-center py-4">
          No goals set yet.
        </p>
      )}

      <button
        type="button"
        onClick={() => navigate(`/portal/goals?from=${department}`)}
        className="mt-3 inline-flex items-center justify-center gap-1 text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
      >
        View all goals <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default DepartmentGoals;
